// O resumo e o funil do Pipeline, cada um em UMA linha.
//
// 01/10/2026 (manhã): o funil nasceu como uma faixa alta de setas, acima do
// quadro, com cinco cartões de indicadores em cima dela.
// 01/10/2026 (tarde), Lucas: "ficou muito cabeçalho sobre cabeçalho e o funil lá
// embaixo espremido. Tem que retirar as redundâncias." Ele tinha razão — o mesmo
// número aparecia três vezes (cartão de indicador, seta do funil, cabeçalho da
// coluna). O que ficou:
//
//  • `ResumoPipeline` — UMA linha para a lista e o kanban: total em aberto, o
//    atalho para as licitações, os três atalhos de cobrança e o resultado do
//    ano. Substitui os cinco cartões.
//  • `FunilEtapas` — a faixa de setas, agora com uma linha de altura, e só na
//    LISTA. No kanban o funil é o próprio cabeçalho das colunas (KanbanPipeline);
//    repetir a faixa em cima dele era mostrar a mesma conta duas vezes.
//
// Cada etapa é um botão: clicar filtra, clicar de novo solta. Fechamento e
// Perdido ficam à direita, separados — são destinos, não etapas do caminho.
import { Tooltip, Tag } from 'antd';
import { WarningOutlined, ClockCircleOutlined, UserOutlined } from '@ant-design/icons';
import { STATUS_LABELS, fmt } from './utils';
import type { KPIs } from './types';
import type { ResumoFunil, EtapaDoFunil, Sinal } from './saudeNegocio';
import { LIMITE_PARADO, valorCompacto } from './saudeNegocio';

const CSS = `
.pf-resumo { display: flex; flex-wrap: wrap; gap: 6px 10px; align-items: center; margin-bottom: 10px; min-height: 24px;
  font-size: 12px; color: #5A6378; }
.pf-resumo b { color: #0E1433; font-weight: 600; font-variant-numeric: tabular-nums; }
.pf-div { width: 1px; height: 14px; background: #E3E6EE; }
.pf-dir { margin-left: auto; display: inline-flex; flex-wrap: wrap; align-items: center; gap: 6px 14px; }
.pf-lic { color: #0930D0; white-space: nowrap; }
.pf-lic:hover { text-decoration: underline; }
.pf-faixa { display: flex; align-items: stretch; gap: 8px; margin-bottom: 10px; overflow-x: auto; padding-bottom: 2px; }
.pf-trilho { display: flex; flex: 1 1 auto; min-width: 0; }
.pf-etapa { flex: 1 0 auto; min-width: 110px; display: flex; align-items: baseline; gap: 7px; border: 0; cursor: pointer;
  font: inherit; color: #0E1433; background: #EEF1F8; padding: 7px 20px 7px 24px; white-space: nowrap;
  clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%);
  margin-left: -7px; transition: background .12s; }
.pf-etapa:first-child { margin-left: 0; padding-left: 12px; border-radius: 6px 0 0 6px;
  clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%); }
.pf-etapa:hover { background: #E1E7F5; }
.pf-etapa:focus-visible, .pf-destino:focus-visible { outline: 2px solid #0930D0; outline-offset: -2px; }
.pf-etapa.pf-vazia { color: #9AA0B4; }
.pf-etapa.pf-ativa { background: #0930D0; color: #fff; }
.pf-etapa.pf-ativa .pf-sub, .pf-etapa.pf-ativa .pf-rot { color: rgba(255,255,255,.85); }
.pf-rot { flex: 0 1 auto; min-width: 0; font-size: 11.5px; font-weight: 600; color: #5A6378;
  overflow: hidden; text-overflow: ellipsis; }
.pf-num { font-size: 13px; font-weight: 700; font-variant-numeric: tabular-nums; }
.pf-sub { margin-left: auto; font-size: 11.5px; color: #5A6378; font-variant-numeric: tabular-nums; }
.pf-fim { display: flex; gap: 6px; flex: none; }
.pf-destino { display: flex; align-items: baseline; gap: 7px; border: 1px solid #E3E6EE; background: #fff; border-radius: 6px;
  padding: 6px 10px; cursor: pointer; font: inherit; color: #0E1433; white-space: nowrap;
  transition: border-color .12s, background .12s; }
.pf-destino:hover { border-color: #B9C0D4; }
.pf-destino.pf-ativa { background: #F4F6FB; border-color: #0930D0; }
/* Tela estreita: a linha de resumo não empilha (viraria seis linhas antes do
   quadro) — fica em uma linha só e rola de lado, como a faixa. */
@media (max-width: 768px) {
  .pf-resumo { flex-wrap: nowrap; overflow-x: auto; white-space: nowrap; padding-bottom: 2px; }
  .pf-resumo > * { flex: none; }
  .pf-dir { flex-wrap: nowrap; margin-left: 0; }
}
`;

const plural = (n: number, um: string, varios: string) => (n === 1 ? um : varios);

// Na faixa de uma linha, nome + quantidade + valor dividem ~200px: os dois nomes
// compridos entram abreviados (o nome inteiro está na dica e na coluna Etapa).
const ROTULO_NA_FAIXA: Record<string, string> = { prospeccao: '1º contato', qualificacao: 'Necessidades' };

function Etapa({ e, ativa, onClick }: { e: EtapaDoFunil; ativa: boolean; onClick: () => void }) {
  const rotulo = STATUS_LABELS[e.status] || e.status;
  const dica = e.qtd === 0
    ? `${rotulo}: nenhum negócio nesta etapa`
    : `${rotulo}: ${e.qtd} ${plural(e.qtd, 'negócio', 'negócios')} · ${fmt(e.valor)} · ponderado ${fmt(e.ponderado)}`
      + (e.parados ? ` · ${e.parados} ${plural(e.parados, 'parado', 'parados')} há mais de ${LIMITE_PARADO} dias` : '');
  return (
    <Tooltip title={dica}>
      <button type="button" onClick={onClick} aria-pressed={ativa}
        className={'pf-etapa' + (ativa ? ' pf-ativa' : '') + (e.qtd === 0 ? ' pf-vazia' : '')}>
        <span className="pf-rot">{ROTULO_NA_FAIXA[e.status] || rotulo}</span>
        <span className="pf-num">{e.qtd}</span>
        {e.valor > 0 && <span className="pf-sub">{valorCompacto(e.valor)}</span>}
      </button>
    </Tooltip>
  );
}

/** A faixa do funil em uma linha. Usada na LISTA (no kanban, o funil são as colunas). */
export function FunilEtapas({ resumo, filtroStatus, setFiltroStatus }: {
  resumo: ResumoFunil;
  filtroStatus: string;
  setFiltroStatus: (s: string) => void;
}) {
  const alternar = (s: string) => setFiltroStatus(filtroStatus === s ? '' : s);
  const destino = (e: EtapaDoFunil, cor: string) => (
    <Tooltip title={`${STATUS_LABELS[e.status]}: ${e.qtd} ${plural(e.qtd, 'negócio', 'negócios')} · ${fmt(e.valor)}`}>
      <button type="button" onClick={() => alternar(e.status)} aria-pressed={filtroStatus === e.status}
        className={'pf-destino' + (filtroStatus === e.status ? ' pf-ativa' : '')}>
        <span className="pf-rot" style={{ color: cor }}>{STATUS_LABELS[e.status]}</span>
        <span className="pf-num">{e.qtd}</span>
      </button>
    </Tooltip>
  );
  return (
    <>
    <style>{CSS}</style>
    <div className="pf-faixa" role="group" aria-label="Funil de vendas por etapa">
      <div className="pf-trilho">
        {resumo.etapas.map(e => (
          <Etapa key={e.status} e={e} ativa={filtroStatus === e.status} onClick={() => alternar(e.status)} />
        ))}
      </div>
      <div className="pf-fim">
        {destino(resumo.ganho, '#0a7f4a')}
        {destino(resumo.perdido, '#8B3A3A')}
      </div>
    </div>
    </>
  );
}

/**
 * A linha de resumo, acima de qualquer vista: quanto há em aberto (dentro dos
 * filtros), os três atalhos de cobrança e, à direita, o resultado do ano.
 *
 * Substitui os cinco cartões de indicadores: "Pipeline ativo" e "Valor
 * ponderado" são a primeira frase; "Contratado no ano" e "Taxa de conversão"
 * são a última; ganhos, perdidos e ticket médio ficam na dica dela.
 *
 * 04/10/2026 — tem que caber em UMA linha na tela do Lucas (1.272 px de área
 * útil: monitor de 1536 com o menu aberto). Antes a parte da direita caía para
 * uma segunda linha e, acima dela, o aviso das licitações ocupava uma terceira.
 * O que mudou: o aviso entra aqui, logo depois dos totais (`depois`); "No ano"
 * perdeu a palavra "contratados" (está na dica); e a ordem dos cartões do
 * kanban, que morava na ponta direita, foi para a linha de filtros
 * (FiltrosPipeline). Em tela mais estreita a direita volta a quebrar.
 */
export function ResumoPipeline({ resumo, contagem, sinal, setSinal, kpis, depois }: {
  resumo: ResumoFunil;
  contagem: { abertos: number; sem_passo: number; parado: number; sem_responsavel: number };
  sinal: Sinal;
  setSinal: (s: Sinal) => void;
  kpis: KPIs | null;
  /** Vai logo depois dos totais — hoje, o atalho para o pipeline de licitações. */
  depois?: React.ReactNode;
}) {
  const item = (chave: Exclude<Sinal, ''>, n: number, texto: string, icone: React.ReactNode, cor: string) => (
    n > 0 || sinal === chave ? (
      <Tag.CheckableTag key={chave} checked={sinal === chave}
        onChange={() => setSinal(sinal === chave ? '' : chave)}
        style={{ margin: 0, border: `1px solid ${sinal === chave ? 'transparent' : '#E3E6EE'}`, fontSize: 12 }}>
        <span style={{ color: sinal === chave ? undefined : cor, marginRight: 5 }}>{icone}</span>
        <span style={{ fontWeight: 600 }}>{n}</span> {texto}
      </Tag.CheckableTag>
    ) : null
  );
  return (
    <>
      <style>{CSS}</style>
      <div className="pf-resumo">
        <Tooltip title={`Em aberto, dentro dos filtros: ${fmt(resumo.abertos.valor)} · ponderado pela probabilidade ${fmt(resumo.abertos.ponderado)}`}>
          <span>
            <b>{resumo.abertos.qtd}</b> em aberto · <b>{valorCompacto(resumo.abertos.valor)}</b> ·
            ponderado <b>{valorCompacto(resumo.abertos.ponderado)}</b>
          </span>
        </Tooltip>
        {depois}
        <span className="pf-div" />
        {item('sem_passo', contagem.sem_passo, 'sem próximo passo', <WarningOutlined />, '#B07500')}
        {item('parado', contagem.parado, `parados há +${LIMITE_PARADO} dias`, <ClockCircleOutlined />, '#B91626')}
        {item('sem_responsavel', contagem.sem_responsavel, 'sem responsável', <UserOutlined />, '#5A6378')}
        {kpis && (
          <span className="pf-dir">
            <Tooltip title={`Contratado no ano, sem os filtros: ${fmt(kpis.receita_contratada_ano)} · ${kpis.ganhos_ano} ${plural(kpis.ganhos_ano, 'ganho', 'ganhos')} · ${kpis.perdidos_ano} ${plural(kpis.perdidos_ano, 'perdido', 'perdidos')} · ticket médio ${fmt(kpis.ticket_medio)}`}>
              <span>
                No ano: <b>{valorCompacto(kpis.receita_contratada_ano)}</b> ·
                conversão <b>{String(kpis.taxa_conversao).replace('.', ',')}%</b>
              </span>
            </Tooltip>
          </span>
        )}
      </div>
    </>
  );
}
